import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  DialogDescription,
  DialogTitle,
  IconButton,
  MenuCloseIcon,
  Separator,
} from '@noor/ui';
import { cn } from '@noor/lib';

/** Modal widths: one entity column, or the live column beside 1–2 proposals. */
export type ReviewModalWidth = 'single' | 'compare' | 'compare-wide';

const WIDTH_CLASS: Record<ReviewModalWidth, string> = {
  single: 'max-w-[680px] sm:max-w-[680px]',
  compare: 'max-w-[760px] sm:max-w-[760px]',
  'compare-wide': 'max-w-[1120px] sm:max-w-[1120px]',
};

interface ReviewModalShellProps {
  title: string;
  subtitle?: string;
  /** Pills row under the title (operation, status, source, submitted). */
  meta?: ReactNode;
  /** Alerts pinned above the content (verdict, warnings). */
  banners?: ReactNode;
  children: ReactNode;
  /** Sticky footer (decision controls); omitted for read-only states. */
  footer?: ReactNode;
  width?: ReviewModalWidth;
  onClose: () => void;
}

/**
 * The one review-modal frame every request renders in (Figma 616:19215 /
 * 616:17952 card): header with title, subtitle, request pills and close;
 * a scrollable body (banners first, then content); an optional pinned
 * footer for the decision. Width follows the content: a single entity
 * column, or the compare grid for edits.
 */
export function ReviewModalShell({
  title,
  subtitle,
  meta,
  banners,
  children,
  footer,
  width = 'single',
  onClose,
}: ReviewModalShellProps) {
  const { t: tCommon } = useTranslation('common');
  return (
    <DialogContent
      showClose={false}
      // No autofocus into the footer controls — focus lands on the dialog
      // container instead, the trap still holds.
      focusOnOpen={false}
      className={cn(
        'flex max-h-[90vh] w-full flex-col gap-0 overflow-hidden rounded-[32px] p-0',
        WIDTH_CLASS[width],
      )}
    >
      <div className="shrink-0 px-5 pt-5 pb-4">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <DialogTitle className="text-2xl leading-[normal] font-semibold text-foreground">
              {title}
            </DialogTitle>
            {subtitle ? (
              <DialogDescription className="text-xs leading-[normal] font-normal text-muted-foreground">
                {subtitle}
              </DialogDescription>
            ) : null}
            {meta}
          </div>
          <IconButton
            variant="ghost"
            aria-label={tCommon('action.close', 'Close')}
            onClick={onClose}
            className="size-11 shrink-0 rounded-lg text-foreground hover:bg-muted hover:text-foreground"
          >
            <MenuCloseIcon className="size-6" />
          </IconButton>
        </div>
      </div>
      <Separator />

      <div
        className="min-h-0 min-w-0 flex-1 overflow-y-auto px-5 py-5"
        data-testid="moderation-review-body"
      >
        <div className="flex flex-col gap-[var(--form-gap-y)]">
          {banners}
          {children}
        </div>
      </div>

      {footer && (
        <div className="w-full shrink-0">
          <Separator />
          <div className="flex flex-col gap-3 px-5 py-4">{footer}</div>
        </div>
      )}
    </DialogContent>
  );
}

/** Info section: semibold title, content, trailing divider. */
export function ReviewSection({
  title,
  children,
  className,
  titleAction,
  divider = true,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  titleAction?: ReactNode;
  divider?: boolean;
}) {
  return (
    <section className={cn('flex flex-col gap-4', className)}>
      <div className="flex items-center justify-between gap-2">
        <ReviewSectionTitle>{title}</ReviewSectionTitle>
        {titleAction}
      </div>
      {children}
      {divider && <Separator />}
    </section>
  );
}

/** Standalone section title for custom blocks. */
export function ReviewSectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-base leading-[1.4] font-semibold text-foreground">{children}</h3>
  );
}

/** Horizontal label/value row (`dl` child) — two equal columns across the section. */
export function InfoRow({
  label,
  value,
  numeric = false,
}: {
  label: string;
  value: ReactNode;
  numeric?: boolean;
}) {
  return (
    <div className="grid w-full grid-cols-2 gap-3 text-sm leading-[1.4]">
      <dt className="font-normal text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'min-w-0 font-normal break-words text-foreground',
          numeric && 'tabular-nums',
        )}
      >
        {value === '' || value === null || value === undefined ? '—' : value}
      </dd>
    </div>
  );
}

/**
 * Stacked label/value row — label above the full-width value. Long text
 * (descriptions) would be squeezed into a rail by the two-column grid.
 */
export function StackedRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex w-full flex-col gap-1.5 text-sm leading-[1.4]">
      <dt className="font-normal text-muted-foreground">{label}</dt>
      <dd className="min-w-0 font-normal break-words whitespace-pre-line text-foreground">
        {value === '' ? '—' : value}
      </dd>
    </div>
  );
}
